import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, IconButton, CircularProgress, Tooltip
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import TodayIcon from '@mui/icons-material/Today';
import axios from 'axios';
import AsignacionFormModal from './AsignacionFormModal';
import { useAuth } from '../context/AuthContext';
import { ASIGNACIONES_API } from '../apiConfig';

const API_BASE_URL = ASIGNACIONES_API.replace(/\/asignaciones$/, '');

// ─── Constants ───────────────────────────────────────────────────────────────
const LABEL_W  = 200;   // px — sticky left column width
const COL_W    = 90;    // px — each time column width
const ROW_H    = 52;    // px — each space row height
const HEADER_H = 52;    // px — header row height

const STATUS_COLORS = {
  Activo:     { bg: '#0057B7', text: '#fff', dot: '#4A8FE7' },
  Programado: { bg: '#d97706', text: '#fff', dot: '#fbbf24' },
  Finalizado: { bg: '#6b7280', text: '#fff', dot: '#9ca3af' },
  Cancelado:  { bg: '#dc2626', text: '#fff', dot: '#f87171' },
};

// ─── Date Utilities ──────────────────────────────────────────────────────────
const addDays    = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const addMonths  = (d, n) => { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; };
const startOfDay = (d)    => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// ─────────────────────────────────────────────────────────────────────────────
export default function InventoryGantt({ espacios = [] }) {
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [viewMode, setViewMode]         = useState('months');
  const [viewCount]                     = useState(12);
  const [viewStart, setViewStart]       = useState(() => {
    const d = startOfDay(new Date()); d.setDate(1); return d;
  });
  const [modalOpen, setModalOpen]       = useState(false);
  const [selectedAsig, setSelectedAsig] = useState(null);

  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetch_ = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/asignaciones?include_all=true&limit=500`);
      setAsignaciones(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch_(); }, []);

  // ── Time columns ──────────────────────────────────────────────────────────
  const timeHeaders = useMemo(() => {
    const h = []; let cur = new Date(viewStart);
    const size = parseInt(viewCount) || 12;
    for (let i = 0; i < size; i++) {
      if (viewMode === 'days') {
        h.push({ label: cur.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' }), start: new Date(cur), end: addDays(cur, 1) });
        cur = addDays(cur, 1);
      } else if (viewMode === 'weeks') {
        const next = addDays(cur, 7);
        h.push({ label: cur.toLocaleDateString('es-DO', { day: 'numeric', month: 'short' }), start: new Date(cur), end: next });
        cur = next;
      } else if (viewMode === 'months') {
        const next = addMonths(cur, 1);
        h.push({ label: cur.toLocaleDateString('es-DO', { month: 'long', year: 'numeric' }), start: new Date(cur), end: next });
        cur = next;
      } else {
        const next = new Date(cur); next.setFullYear(next.getFullYear() + 1);
        h.push({ label: cur.getFullYear().toString(), start: new Date(cur), end: next });
        cur = next;
      }
    }
    return h;
  }, [viewMode, viewStart, viewCount]);

  const viewEnd         = timeHeaders.length ? timeHeaders[timeHeaders.length - 1].end : new Date();
  const totalMs         = viewEnd - viewStart;
  const timelineW       = COL_W * timeHeaders.length;
  const totalW          = LABEL_W + timelineW;

  // ── Navigation ─────────────────────────────────────────────────────────────
  const navigate = dir => setViewStart(prev => {
    const m = dir === 'fwd' ? 1 : -1;
    if (viewMode === 'days')   return addDays(prev, 15 * m);
    if (viewMode === 'weeks')  return addDays(prev, 28 * m);
    if (viewMode === 'months') return addMonths(prev, 3 * m);
    const d = new Date(prev); d.setFullYear(d.getFullYear() + m); return d;
  });

  const jumpToday = () => {
    const d = startOfDay(new Date());
    if (viewMode === 'months') d.setDate(1);
    setViewStart(d);
  };

  const zoom = inOut => {
    const order = ['days', 'weeks', 'months', 'year'];
    const idx = order.indexOf(viewMode);
    if (inOut === 'in'  && idx > 0)              setViewMode(order[idx - 1]);
    if (inOut === 'out' && idx < order.length-1) setViewMode(order[idx + 1]);
  };

  // ── Bar position ──────────────────────────────────────────────────────────
  const barStyle = (asig) => {
    const s = new Date(asig.fecha_inicio), e = new Date(asig.fecha_fin);
    if (e < viewStart || s > viewEnd) return null;
    const left  = Math.max(0, (s - viewStart) / totalMs * timelineW);
    const right = Math.max(0, (viewEnd - e)   / totalMs * timelineW);
    return { left, width: Math.max(4, timelineW - left - right) };
  };

  const todayLeft = useMemo(() => {
    const now = new Date();
    if (now < viewStart || now > viewEnd) return null;
    return (now - viewStart) / totalMs * timelineW;
  }, [viewStart, viewEnd, totalMs, timelineW]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <CircularProgress />
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#F8F9FA' }}>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 2.5, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)',
        flexShrink: 0,
      }}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="primary" sx={{ letterSpacing: '-0.02em' }}>
            Cronograma Interactivo
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {espacios.length} vallas · {asignaciones.length} contratos
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          {/* Mode pills */}
          <Box sx={{ display: 'flex', bgcolor: '#F0F4FF', borderRadius: 2, p: 0.5, border: '1px solid rgba(0,87,183,0.15)' }}>
            {['days','weeks','months','year'].map(m => (
              <Box key={m} onClick={() => setViewMode(m)} sx={{
                px: 1.5, py: 0.4, borderRadius: 1.5, cursor: 'pointer',
                fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', transition: 'all 0.18s',
                bgcolor: viewMode === m ? '#0057B7' : 'transparent',
                color:   viewMode === m ? '#fff' : '#4F5660',
                '&:hover': { bgcolor: viewMode === m ? '#0057B7' : 'rgba(0,87,183,0.1)', color: viewMode === m ? '#fff' : '#0057B7' },
              }}>
                {m === 'days' ? 'Días' : m === 'weeks' ? 'Semanas' : m === 'months' ? 'Meses' : 'Año'}
              </Box>
            ))}
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', bgcolor: '#F0F4FF', borderRadius: 2, border: '1px solid rgba(0,87,183,0.15)' }}>
            <IconButton size="small" onClick={() => navigate('bwd')} sx={{ color: '#4F5660', '&:hover': { color: '#0057B7' } }}>
              <ArrowBackIosIcon sx={{ fontSize: 13 }} />
            </IconButton>
            <Tooltip title="Ir a hoy">
              <IconButton size="small" onClick={jumpToday} sx={{ color: '#0057B7' }}>
                <TodayIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => navigate('fwd')} sx={{ color: '#4F5660', '&:hover': { color: '#0057B7' } }}>
              <ArrowForwardIosIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>

          {/* Zoom */}
          <Box sx={{ display: 'flex', bgcolor: '#F0F4FF', borderRadius: 2, border: '1px solid rgba(0,87,183,0.15)' }}>
            <Tooltip title="Acercar"><span>
              <IconButton size="small" onClick={() => zoom('in')} disabled={viewMode === 'days'}
                sx={{ color: '#4F5660', '&:hover': { color: '#0057B7' }, '&.Mui-disabled': { color: '#c5d1e8' } }}>
                <ZoomInIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Alejar"><span>
              <IconButton size="small" onClick={() => zoom('out')} disabled={viewMode === 'year'}
                sx={{ color: '#4F5660', '&:hover': { color: '#0057B7' }, '&.Mui-disabled': { color: '#c5d1e8' } }}>
                <ZoomOutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span></Tooltip>
          </Box>
        </Box>
      </Box>

      {/* ── Single shared scroll container ──────────────────────────────── */}
      {/*
        KEY DESIGN: One Box handles BOTH scroll axes.
        - Label column cells: position:sticky, left:0 → always visible
        - Header row:         position:sticky, top:0  → always visible
        - Everything else scrolls normally inside one container
        → Labels and timeline rows are always in sync because they live
          in the same DOM scroll ancestor.
      */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        '&::-webkit-scrollbar': { width: 7, height: 7 },
        '&::-webkit-scrollbar-track': { bgcolor: '#F0F0F0' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#b0bec5', borderRadius: 4 },
      }}>
        {/* Inner container — wider than viewport → triggers horizontal scroll */}
        <Box sx={{ minWidth: totalW, position: 'relative' }}>

          {/* ── Sticky header row ──────────────────────────────────────── */}
          <Box sx={{
            display: 'flex',
            position: 'sticky', top: 0, zIndex: 30,
            height: HEADER_H,
            bgcolor: '#EEF2F7',
            borderBottom: '2px solid rgba(0,87,183,0.15)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            {/* Corner cell */}
            <Box sx={{
              width: LABEL_W, flexShrink: 0,
              position: 'sticky', left: 0, zIndex: 40,
              bgcolor: '#EEF2F7',
              display: 'flex', alignItems: 'center', px: 2,
              borderRight: '2px solid rgba(0,87,183,0.15)',
            }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#0057B7', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Espacio / Valla
              </Typography>
            </Box>

            {/* Time columns header */}
            {timeHeaders.map((h, i) => {
              const isCur = h.start <= new Date() && new Date() < h.end;
              return (
                <Box key={i} sx={{
                  width: COL_W, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRight: '1px solid rgba(0,0,0,0.06)',
                  bgcolor: isCur ? 'rgba(0,87,183,0.1)' : 'transparent',
                  px: 0.5,
                }}>
                  <Typography sx={{
                    fontSize: viewMode === 'months' ? '0.6rem' : '0.66rem',
                    fontWeight: 700, textAlign: 'center',
                    color: isCur ? '#0057B7' : '#4F5660',
                    textTransform: viewMode === 'months' ? 'capitalize' : 'none',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%',
                  }}>
                    {h.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* ── Space rows ─────────────────────────────────────────────── */}
          {espacios.map((esp, ri) => {
            const asigs = asignaciones.filter(a => a.codigo_espacio === esp.Codigo);
            const hasContracts = asigs.length > 0;
            return (
              <Box key={esp.Codigo} sx={{
                display: 'flex',
                height: ROW_H,
                bgcolor: ri % 2 === 0 ? '#ffffff' : '#F8F9FA',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                '&:hover': { bgcolor: 'rgba(0,87,183,0.04)' },
                transition: 'background 0.12s',
              }}>

                {/* ── Sticky label cell ───────────────────────────────── */}
                <Box sx={{
                  width: LABEL_W, flexShrink: 0,
                  position: 'sticky', left: 0, zIndex: 20,
                  bgcolor: 'inherit',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  px: 2, borderRight: '2px solid rgba(0,87,183,0.1)',
                }}>
                  <Typography sx={{ fontSize: '0.77rem', fontWeight: 700, color: hasContracts ? '#1A1A1B' : '#6b7280', lineHeight: 1.2 }}>
                    {esp.Codigo}
                  </Typography>
                  <Typography sx={{ fontSize: '0.63rem', color: '#9ca3af', mt: 0.2, lineHeight: 1.2 }}>
                    {esp.Tipo_Espacio} · {esp.Provincia}
                  </Typography>
                </Box>

                {/* ── Timeline cell ────────────────────────────────────── */}
                <Box sx={{ flexGrow: 1, position: 'relative', minWidth: timelineW }}>

                  {/* Grid lines */}
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none' }}>
                    {timeHeaders.map((h, i) => {
                      const isCur = h.start <= new Date() && new Date() < h.end;
                      return (
                        <Box key={i} sx={{
                          width: COL_W, flexShrink: 0, height: '100%',
                          borderRight: '1px solid rgba(0,0,0,0.04)',
                          bgcolor: isCur ? 'rgba(0,87,183,0.03)' : 'transparent',
                        }} />
                      );
                    })}
                  </Box>

                  {/* Today line */}
                  {todayLeft !== null && (
                    <Box sx={{
                      position: 'absolute', top: 0, bottom: 0,
                      left: todayLeft, width: 2, zIndex: 10, pointerEvents: 'none',
                      bgcolor: '#e53935',
                      boxShadow: '0 0 6px rgba(229,57,53,0.5)',
                    }} />
                  )}

                  {/* Empty label */}
                  {asigs.length === 0 && (
                    <Typography sx={{
                      position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                      fontSize: '0.63rem', color: 'rgba(0,150,100,0.5)', fontWeight: 600,
                      pointerEvents: 'none', userSelect: 'none',
                    }}>
                      ✓ Disponible
                    </Typography>
                  )}

                  {/* Contract bars */}
                  {asigs.map((asig, bi) => {
                    const bs = barStyle(asig);
                    if (!bs) return null;
                    const colors = STATUS_COLORS[asig.estado] || { bg: '#0057B7', text: '#fff', dot: '#4A8FE7' };
                    const barH   = asigs.length > 1 ? 17 : 28;
                    const topOff = asigs.length > 1 ? 4 + bi * 21 : (ROW_H - barH) / 2;
                    return (
                      <Tooltip key={asig.id} placement="top" arrow title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{asig.cliente}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', opacity: 0.85 }}>{asig.fecha_inicio} → {asig.fecha_fin}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', opacity: 0.85 }}>{asig.tipo_venta} · {asig.estado}</Typography>
                          {asig.monto_total && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#60a5fa', fontWeight: 700 }}>
                              {new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(asig.monto_total)}
                            </Typography>
                          )}
                        </Box>
                      }>
                        <Box onClick={() => { setSelectedAsig(asig); setModalOpen(true); }} sx={{
                          position: 'absolute',
                          top: topOff, left: bs.left + 2,
                          width: Math.max(4, bs.width - 4), height: barH,
                          bgcolor: colors.bg, color: colors.text,
                          borderRadius: 1,
                          display: 'flex', alignItems: 'center', px: 0.8, gap: 0.6,
                          overflow: 'hidden', cursor: 'pointer', zIndex: 5,
                          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          transition: 'all 0.15s',
                          '&:hover': { filter: 'brightness(1.12)', transform: 'scaleY(1.06)', zIndex: 15, boxShadow: '0 3px 12px rgba(0,0,0,0.2)' },
                        }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colors.dot, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {asig.cliente}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Legend ──────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', gap: 2.5, px: 3, py: 1, flexShrink: 0, flexWrap: 'wrap',
        bgcolor: '#fff', borderTop: '1px solid rgba(0,0,0,0.07)', alignItems: 'center',
      }}>
        {Object.entries(STATUS_COLORS).map(([s, { dot }]) => (
          <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#4F5660', fontWeight: 600 }}>{s}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, ml: 0.5 }}>
          <Box sx={{ width: 12, height: 2, bgcolor: '#e53935', borderRadius: 1 }} />
          <Typography sx={{ fontSize: '0.7rem', color: '#4F5660', fontWeight: 600 }}>Hoy</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.68rem', color: '#adb5bd', ml: 'auto' }}>
          Clic en una barra para editar
        </Typography>
      </Box>

      {/* ── Modal ───────────────────────────────────────────────────────── */}
      {modalOpen && (
        <AsignacionFormModal
          open={modalOpen}
          onClose={() => { setModalOpen(false); setSelectedAsig(null); }}
          onSaved={fetch_}
          initialData={selectedAsig}
          espacios={espacios}
          readOnly={selectedAsig && !isAdmin && selectedAsig.created_by && selectedAsig.created_by !== currentUser?.uid}
        />
      )}
    </Box>
  );
}
